/**
 * ModelRoutingPanel — AI Model Routing Configuration
 *
 * Shows which model is used for each AI service (text refinement,
 * report generation, diagnosis classification, AI copilot).
 * Allows administrators to update primary/fallback model assignments.
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  SwapHoriz as SwapIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { updateRoutingEntry, fetchAIConfig } from '../../../store/aiServicesSlice';
import aiServicesConfigService from '../../../services/aiServicesConfigService';
import type { RoutingEntry, ModelInfo } from '../../../services/aiServicesConfigService';
import { typography } from '~/theme';

interface Props {
  onSnack: (message: string, severity: 'success' | 'error' | 'info') => void;
}

const SERVICE_META: Record<string, { label: string; desc: string; icon: string }> = {
  text_refinement: {
    label: 'Text Refinement',
    desc: 'Real-time transcript correction and medical terminology normalization',
    icon: '✏️',
  },
  report_generation: {
    label: 'Report Generation',
    desc: 'Structured radiology report generation (findings, impression)',
    icon: '📋',
  },
  diagnosis_classification: {
    label: 'Diagnosis Classification',
    desc: 'Pathology type and diagnosis category classification',
    icon: '🏥',
  },
  ai_copilot: {
    label: 'AI Copilot',
    desc: 'Conversational AI reasoning, intent parsing, and query planning',
    icon: '🤖',
  },
};

const PROVIDER_LABELS: Record<string, string> = {
  assemblyai: 'AssemblyAI',
  openai: 'OpenAI',
};

const getProviderModels = (
  providerModels: Record<string, ModelInfo[]>,
  providerId: string,
): ModelInfo[] => providerModels[providerId] ?? [];

const ModelRoutingPanel: React.FC<Props> = ({ onSnack }) => {
  const [providerModels, setProviderModels] = useState<Record<string, ModelInfo[]>>({});
  const dispatch = useAppDispatch();
  const { routing, saving, loading } = useAppSelector((s) => s.aiServices);
  const [editingService, setEditingService] = useState<string | null>(null);
  const [editState, setEditState] = useState<Partial<RoutingEntry>>({});

  useEffect(() => {
    const fetchModels = async () => {
      const [assemblyai, openai] = await Promise.allSettled([
        aiServicesConfigService.getProviderModels('assemblyai'),
        aiServicesConfigService.getProviderModels('openai'),
      ]);
      setProviderModels({
        assemblyai: assemblyai.status === 'fulfilled' ? assemblyai.value.models : [],
        openai: openai.status === 'fulfilled' ? openai.value.models : [],
      });
    };
    fetchModels();
  }, []);

  const handleStartEdit = (entry: RoutingEntry) => {
    setEditingService(entry.service);
    setEditState({
      primary_provider: entry.primary_provider,
      primary_model: entry.primary_model,
      fallback_provider: entry.fallback_provider,
      fallback_model: entry.fallback_model,
    });
  };

  const handleCancelEdit = () => {
    setEditingService(null);
    setEditState({});
  };

  const handleSaveRouting = async (service: string) => {
    try {
      await dispatch(updateRoutingEntry({
        service,
        primary_provider: editState.primary_provider,
        primary_model: editState.primary_model,
        fallback_provider: editState.fallback_provider,
        fallback_model: editState.fallback_model,
      })).unwrap();
      setEditingService(null);
      setEditState({});
      onSnack(`Routing updated for ${SERVICE_META[service]?.label || service}`, 'success');
    } catch (err) {
      onSnack(String(err), 'error');
    }
  };

  const handleRefresh = () => {
    dispatch(fetchAIConfig());
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">AI Model Routing</Typography>
            <Typography variant="body2" color="text.secondary">
              Define which model is used for each AI service. Each service has a primary and fallback configuration.
            </Typography>
          </Box>
          <Tooltip title="Refresh routing table">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Divider />

        {/* Routing Table */}
        {routing.map((entry) => {
          const meta = SERVICE_META[entry.service] || {
            label: entry.service,
            desc: '',
            icon: '⚙️',
          };
          const isEditing = editingService === entry.service;

          return (
            <Card key={entry.service} variant="outlined">
              <CardHeader
                title={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" component="span">{meta.icon}</Typography>
                    <Typography variant="subtitle1" fontWeight={typography.label.fontWeight}>{meta.label}</Typography>
                  </Stack>
                }
                subheader={meta.desc}
                action={
                  isEditing ? (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={handleCancelEdit}>Cancel</Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                        onClick={() => handleSaveRouting(entry.service)}
                        disabled={saving}
                      >
                        Save
                      </Button>
                    </Stack>
                  ) : (
                    <Button size="small" variant="outlined" onClick={() => handleStartEdit(entry)}>
                      Edit
                    </Button>
                  )
                }
              />
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  {/* Primary */}
                  <Grid item xs={12} md={5}>
                    <Card
                      variant="outlined"
                      sx={{ bgcolor: 'primary.50', borderColor: 'primary.200' }}
                    >
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="caption" color="primary.main" fontWeight={typography.label.fontWeight}>
                          PRIMARY
                        </Typography>
                        {isEditing ? (
                          <Stack spacing={1.5} sx={{ mt: 1 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Provider</InputLabel>
                              <Select
                                value={editState.primary_provider || ''}
                                label="Provider"
                                onChange={(e) => {
                                  const newProvider = e.target.value;
                                  const firstModel = getProviderModels(providerModels, newProvider)[0]?.id || '';
                                  setEditState((prev: Partial<RoutingEntry>) => ({
                                    ...prev,
                                    primary_provider: newProvider,
                                    primary_model: firstModel,
                                  }));
                                }}
                              >
                                <MenuItem value="assemblyai">AssemblyAI</MenuItem>
                                <MenuItem value="openai">OpenAI</MenuItem>
                              </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                              <InputLabel>Model</InputLabel>
                              <Select
                                value={editState.primary_model || ''}
                                label="Model"
                                onChange={(e) => setEditState((prev: Partial<RoutingEntry>) => ({ ...prev, primary_model: e.target.value }))}
                              >
                                {getProviderModels(providerModels, editState.primary_provider || '').map((m) => (
                                  <MenuItem key={m.id} value={m.id}>{m.display_name || m.id}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Stack>
                        ) : (
                          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                            <Typography variant="body2" fontWeight={typography.button.fontWeight}>
                              {PROVIDER_LABELS[entry.primary_provider] || entry.primary_provider}
                            </Typography>
                            <Chip
                              label={entry.primary_model}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace', fontSize: typography.caption.fontSize }}
                            />
                          </Stack>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Arrow */}
                  <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <SwapIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
                  </Grid>

                  {/* Fallback */}
                  <Grid item xs={12} md={5}>
                    <Card
                      variant="outlined"
                      sx={{ bgcolor: 'warning.50', borderColor: 'warning.200' }}
                    >
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="caption" color="warning.main" fontWeight={typography.label.fontWeight}>
                          FALLBACK
                        </Typography>
                        {isEditing ? (
                          <Stack spacing={1.5} sx={{ mt: 1 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Provider</InputLabel>
                              <Select
                                value={editState.fallback_provider || ''}
                                label="Provider"
                                onChange={(e) => {
                                  const newProvider = e.target.value;
                                  const firstModel = getProviderModels(providerModels, newProvider)[0]?.id || '';
                                  setEditState((prev: Partial<RoutingEntry>) => ({
                                    ...prev,
                                    fallback_provider: newProvider,
                                    fallback_model: firstModel,
                                  }));
                                }}
                              >
                                <MenuItem value="assemblyai">AssemblyAI</MenuItem>
                                <MenuItem value="openai">OpenAI</MenuItem>
                              </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                              <InputLabel>Model</InputLabel>
                              <Select
                                value={editState.fallback_model || ''}
                                label="Model"
                                onChange={(e) => setEditState((prev: Partial<RoutingEntry>) => ({ ...prev, fallback_model: e.target.value }))}
                              >
                                {getProviderModels(providerModels, editState.fallback_provider || '').map((m) => (
                                  <MenuItem key={m.id} value={m.id}>{m.display_name || m.id}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Stack>
                        ) : (
                          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                            <Typography variant="body2" fontWeight={typography.button.fontWeight}>
                              {PROVIDER_LABELS[entry.fallback_provider] || entry.fallback_provider}
                            </Typography>
                            <Chip
                              label={entry.fallback_model}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace', fontSize: typography.caption.fontSize }}
                            />
                          </Stack>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          );
        })}

        {routing.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No routing entries configured.</Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default ModelRoutingPanel;
